import { PsNotificationIcon } from './PsNotificationIcon';
import { I18nUtils } from '../../../i18n/I18nUtils';
import DateFormatter from '../../shared/utils/DateFormatter';
import { PsUser } from '../shared/model/PsUser';
import { PsCompany } from '../shared/model/PsCompany';
import { PsWork } from '../shared/model/PsWork';
import { PsWorker } from '../shared/model/PsWorker';
import { PsMachine } from '../shared/model/PsMachine';
import { PsDocument } from '../shared/model/PsDocument';
import { PsOrigin } from '../shared/model/PsOrigin';

export class PsNotification {

    _id; // number
    _date; // Date
    _read; // boolean
    _icon; // PsNotificationIcon
    _title; // string
    _solved; // string
    _message; // string
    _expiration; // Date | null
    _fromMyContractors = false; // boolean
    _listeriaDate; // Date | null

    _user = null; // PsUser | null
    _work = null; // PsWork | null
    _worker = null; // PsWorker | null
    _company = null; // PsCompany | null
    _machine = null; // PsMachine | null
    _document = null; // PsDocument | null

    constructor(
        id,
        date,
        read,
        icon,
        title,
        solved,
        message,
        expiration,
        fromMyContractors,
        listeriaDate,
    ) {
        this._id = id;
        this._date = date;
        this._read = read;
        this._icon = icon;
        this._title = title;
        this._solved = solved;
        this._message = message;
        this._expiration = expiration;
        this._fromMyContractors = fromMyContractors;
        this._listeriaDate = listeriaDate;
    }

    static fromAPINotifications(notifications) {
        return notifications.map((notification) => PsNotification.fromAPINotification(notification));
    }

    static fromAPINotification(notification) {
        const {
            id,
            date,
            is_read,
            title,
            solved,
            message,
            expiry_date,
            source_user,
            company,
            construction,
            worker,
            machine,
            document,
            as_subcontracted,
            listeria_date,
        } = notification;

        const psNotification = new this(
            id,
            date,
            is_read,
            PsNotificationIcon.fromAPINotification(notification),
            title || '',
            solved,
            message || '',
            expiry_date || null,
            as_subcontracted,
            listeria_date || null
        );

        if ( source_user ) {
            psNotification._user = PsUser.fromAPIUser(source_user);
        }

        if ( company ) {
            psNotification._company = PsCompany.fromAPICompany(company);

            if ( construction ) {
                psNotification._work = PsWork.fromAPIWork(construction);
            }

            if ( worker ) {
                psNotification._worker = PsWorker.fromAPIWorker(worker);
                psNotification._worker.assignCompany(psNotification._company);
                psNotification._worker.assignOrigin(
                    PsOrigin.fromValues(
                        {
                            companyId: psNotification._company.id(),
                            workerId: psNotification._worker.id(),
                            fromMyContractors: worker.as_subcontracted,
                        },
                    ),
                );
            }

            if ( machine ) {
                psNotification._machine = PsMachine.fromAPIMachine(machine);
                psNotification._machine.assignCompany(psNotification._company);
                psNotification._machine.assignOrigin(
                    PsOrigin.fromValues(
                        {
                            companyId: psNotification._company.id(),
                            machineId: psNotification._machine.id(),
                            fromMyContractors: machine.as_subcontracted,
                        },
                    ),
                );
            }

            if ( document ) {
                psNotification._document = PsDocument.fromAPIDocument(document);

                if ( document.worker ) {
                    psNotification._worker = PsWorker.fromAPIWorker(document.worker);
                    psNotification._worker.assignCompany(psNotification._company);
                    psNotification._worker.assignOrigin(
                        PsOrigin.fromValues(
                            {
                                companyId: psNotification._company.id(),
                                workerId: psNotification._worker.id(),
                                fromMyContractors: document.as_subcontracted,
                            },
                        ),
                    );
                }

                if ( document.machine ) {
                    psNotification._machine = PsMachine.fromAPIMachine(document.machine);
                    psNotification._machine.assignCompany(psNotification._company);
                    psNotification._machine.assignOrigin(
                        PsOrigin.fromValues(
                            {
                                companyId: psNotification._company.id(),
                                machineId: psNotification._machine.id(),
                                fromMyContractors: document.as_subcontracted,
                            },
                        ),
                    );
                }

                psNotification._document.assignOrigin(
                    PsOrigin.fromValues(
                        {
                            companyId: psNotification._company.id(),
                            workerId: document.worker ? document.worker.id : undefined,
                            machineId: document.machine ? document.machine.id : undefined,
                            documentId: document.id,
                            fromMyContractors: document.as_subcontracted,
                        },
                    ),
                );
            }
        }

        return psNotification;
    }

    id() {
        return this._id;
    }

    date() {
        return this._date;
    }

    icon() {
        return this._icon.icon();
    }

    isRead() {
        return this._read;
    }

    title() {
        return this._title;
    }

    isSolved() {
        return this._solved;
    }

    setSolved(solved) {
        this._solved = solved;
    }

    message() {
        return this._message;
    }

    expiration() {
        return this._expiration;
    }

    canExpire() {
        return this._expiration instanceof Date;
    }

    dateDescription() {
        return this.canExpire()
            ? `${I18nUtils.tr('TR_CADUCA_EL')} ${DateFormatter.formatDate(this.expiration())}`
            : DateFormatter.formatDatetime(this.date());
    }

    user() {
        return this._user;
    }

    hasUser() {
        return !!this.user();
    }

    work() {
        return this._work;
    }

    hasWork() {
        return !!this.work();
    }

    worker() {
        return this._worker;
    }

    hasWorker() {
        return !!this.worker();
    }

    company() {
        return this._company;
    }

    hasCompany() {
        return !!this.company();
    }

    machine() {
        return this._machine;
    }

    hasMachine() {
        return !!this.machine();
    }

    document() {
        return this._document;
    }

    hasDocument() {
        return !!this.document();
    }

    isFromMyContractors() {
        return this._fromMyContractors;
    }

    isFromMyOutsources() {
        return !this._fromMyContractors;
    }

    listeriaDate() {
        return this._listeriaDate;
    }

    listeriaDateDescription() {
        return DateFormatter.formatDate(this.listeriaDate());
    }
}



// WEBPACK FOOTER //
// ./src/app/messages-notifications/notifications/PsNotification.js